import { ReactNode } from 'react'
import { FloatingVoiceControls } from './FloatingVoiceControls'
import type { ConnectionStatus, VoiceStatus } from '../hooks/usePeerJS'
import '../styles/GameLayout.css'

interface VoiceControls {
  connectionStatus: ConnectionStatus
  voiceStatus: VoiceStatus
  toggleMute: () => void
  connect?: () => void
  disconnect?: () => void
}

interface GameLayoutProps {
  // Voice controls props
  voice: VoiceControls
  showVoiceControls?: boolean

  // Content
  children: ReactNode

  // Optional className for custom styling
  className?: string
}

/**
 * GameLayout - Standardized voice controls wrapper for all multiplayer games
 *
 * Provides:
 * - FloatingVoiceControls at bottom (Zoom/Meet style)
 * - Consistent voice UX across all games
 * - Single source of truth for voice control updates
 *
 * Benefits:
 * - Update voice controls once → cascades to ALL games
 * - Consistent user experience
 * - Less code duplication
 *
 * Usage:
 * ```tsx
 * <GameLayout voice={voice}>
 *   <GameHeader ... />
 *   <GameContext ... />
 *   <YourGameContent />
 *   <ChatWidget ... />
 * </GameLayout>
 * ```
 */
export function GameLayout({
  voice,
  showVoiceControls = true,
  children,
  className = '',
}: GameLayoutProps) {
  return (
    <div className={`game-layout ${className}`}>
      {/* Main content area - game renders everything here */}
      {children}

      {/* Voice controls - Fixed at bottom, consistent across all games */}
      {showVoiceControls && (
        <FloatingVoiceControls
          connectionStatus={voice.connectionStatus}
          voiceStatus={voice.voiceStatus}
          onToggleMute={voice.toggleMute}
          onConnect={voice.connect}
          onDisconnect={voice.disconnect}
        />
      )}
    </div>
  )
}
